import { CSSProperties, Fragment, RefObject, useRef } from "react";

import { EUserType, type IChat } from "@dreamcam/messenger-sdk";
import type { Set } from "immutable";

import { Image } from "@components/Image";
import { Typography } from "@components/Typography";

import { useMessengerStore } from "@storage/global/useMessengerStore";
import { useGetAllCategoriesUsersQuery } from "@storage/queries/messenger";

import { ICON_PATHS } from "@configs/icons";

import { ChatMenu } from "./ChatMenu";
import { dateConverter } from "./helpers";

interface IChatListProps {
	chatList: IChat[];
	activeChat: string | null;
	setActivePanel: (isOpenChatList: boolean, currentChat: string | null) => void;
	typingStatusList: Set<string>;
	containerRef: RefObject<HTMLDivElement | null>;
}

export const ChatList = ({ chatList, activeChat, setActivePanel, typingStatusList }: IChatListProps) => {
	const { userMsgData } = useMessengerStore();
	const chatContainerRef = useRef<HTMLDivElement>(null);
	const { setMessengerUnsupportedByUser, setIsMessagesBoxFolded } = useMessengerStore();
	const categoriesListQuery = useGetAllCategoriesUsersQuery();

	const setCategoryByUser = (category: number) => {
		if (!categoriesListQuery.isSuccess) return;

		const findCategory = categoriesListQuery.data.find((item) => item.id === category);

		if (findCategory) return findCategory.color;
		else return "";
	};

	const onChangeChat = (chat: string) => {
		const findDropdownMenu = document.getElementById("dropdown-menu-msg-chat-content");
		if (findDropdownMenu) return;

		setMessengerUnsupportedByUser({ nickname: "", isDisabled: false, categoryColor: "" });
		setIsMessagesBoxFolded(false);
		setActivePanel(false, chat);
	};

	const renderChatList = () => (
		<div className="flex h-full flex-col items-stretch gap-2 p-0">
			<div className="overflow-auto">
				{chatList.length > 0
					? chatList.map((chat, chatIndex) => <Fragment key={chat.id}>{renderChat(chat, chatIndex)}</Fragment>)
					: null}
			</div>
		</div>
	);

	const renderChat = (chat: IChat, chatIndex: number) => {
		const nameOfParticipant = chat.participant.username || chat.participant.user_chat_client_id;
		if (nameOfParticipant === null || chat.last_message === null) return null;

		const isNewChat = chat.last_message.sender_id.length === 0;
		const timeOfMessage = dateConverter(isNewChat, chat.last_message.sent_at);
		const imageName = nameOfParticipant[0]?.toUpperCase();

		const bannedCompanionStyle = chat.participant.block_data.is_blocked ? { opacity: 0.3 } : { opacity: 1 };
		const isAttachment = chat.last_message.content === "" && chat.last_message.attachments.length;
		const isBanned = chat.participant.block_data.is_blocked;
		const isMuted = chat.participant.mute_data.is_muted;

		return (
			<div
				key={chat.id}
				style={
					{
						"--num-of-above-items": chatIndex,
						...bannedCompanionStyle,
					} as CSSProperties
				}
				ref={chatContainerRef}
				onClick={() => onChangeChat(chat.id)}
				className={
					activeChat === chat.id
						? `flex min-h-10 cursor-pointer items-center justify-between gap-4 bg-secondary px-5 py-2.5
							hover:bg-secondary`
						: "flex min-h-10 cursor-pointer items-center justify-between gap-4 px-5 py-2.5 hover:bg-secondary"
				}
			>
				<div className="flex w-full gap-4">
					<div
						className={`relative flex size-12.5 items-center justify-center rounded-full bg-ground-hover text-xl
							font-bold text-white max-[575px]:h-10 max-[575px]:w-10`}
						style={
							userMsgData.user_type === EUserType.MODEL
								? {
										background: setCategoryByUser(chat.participant.category || 0),
									}
								: {
										backgroundImage: `url(${chat.participant.avatar})`,
										backgroundSize: "cover",
										backgroundPosition: "center",
									}
						}
					>
						<p>{userMsgData.user_type === EUserType.MODEL ? imageName : null}</p>

						{chat.participant.is_online ? (
							<span className="absolute right-0 bottom-0">
								<Image width={15} height={15} src={ICON_PATHS.STATUS_ONLINE} alt="status" />
							</span>
						) : null}
					</div>
					<div className="flex w-full max-w-56.5 flex-col gap-[1.5px] max-[575px]:max-w-47.5">
						<div
							className={
								isBanned || isMuted
									? "inline-flex items-center font-[Inter] text-base leading-5.5 font-bold"
									: "inline-flex font-[Inter] text-base leading-5.5 font-bold"
							}
						>
							<div className="flex items-center">
								<span
									className="inline-block max-w-37.5 overflow-hidden text-ellipsis whitespace-nowrap"
									style={{
										fontWeight:
											chat.last_message.read_at === null && chat.last_message.sender_id !== userMsgData.chat_client_id
												? 700
												: 400,
										color: setCategoryByUser(chat.participant.category || 0),
									}}
								>
									{nameOfParticipant}
								</span>
								<span className="m-0 mx-2 inline-block text-center text-xs text-white">•</span>
								<Typography
									type="span"
									className="m-0"
									style={{
										fontWeight:
											chat.last_message.read_at === null && chat.last_message.sender_id !== userMsgData.chat_client_id
												? 700
												: 400,
									}}
								>
									{timeOfMessage}
								</Typography>

								{isBanned ? (
									<span className="ml-2">
										<Image width={13} height={13} src={ICON_PATHS.BANNED} alt="blocked" />
									</span>
								) : null}

								{isMuted ? (
									<span className="ml-2.5">
										<Image width={14} height={15} src={ICON_PATHS.MUTED} alt="muted" />
									</span>
								) : null}
							</div>
						</div>

						{typingStatusList.has(chat.id) ? (
							<Typography
								type="span"
								className={`animate-typing-dots inline-flex gap-1 overflow-hidden font-[Inter] text-base leading-5.5
									font-normal text-ellipsis whitespace-nowrap text-primary`}
							>
								typing
							</Typography>
						) : (
							<>
								{chat.draft_message.content.length > 0 || chat.last_message.id === "0" ? (
									<span
										className={`inline-flex gap-1 overflow-hidden font-[Inter] text-base leading-5.5 font-bold
											text-ellipsis whitespace-nowrap text-white`}
									>
										<p className="text-error-400">Draft:</p>
										{chat.draft_message.content}
									</span>
								) : (
									<>
										{isAttachment ? (
											<div className="flex items-center">
												<span className="mr-2.5">
													<Image width={16} height={16} src={ICON_PATHS.COIN} alt="coin" />
												</span>
												<p
													className={`inline-flex gap-1 overflow-hidden font-[Inter] text-base leading-5.5 text-ellipsis
														whitespace-nowrap text-[#EED16B]`}
													style={{
														fontWeight:
															chat.last_message.read_at === null &&
															chat.last_message.sender_id !== userMsgData.chat_client_id
																? 700
																: 400,
													}}
												>
													{`${chat.last_message.sender_id !== userMsgData.chat_client_id ? "Sent" : "You sent"} 
													${chat.last_message.attachments[0].meta.amount} tk`}
												</p>
											</div>
										) : (
											<p
												className={`inline-flex gap-1 overflow-hidden font-[Inter] text-base leading-5.5 text-ellipsis
													whitespace-nowrap`}
												style={
													chat.last_message.read_at === null &&
													chat.last_message.sender_id !== userMsgData.chat_client_id
														? { fontWeight: 700, color: "white" }
														: { fontWeight: 400, color: "#B8B0BC" }
												}
											>
												{chat.last_message.content}
											</p>
										)}
									</>
								)}
							</>
						)}
					</div>
				</div>

				<ChatMenu currentChat={chat} />
			</div>
		);
	};

	return <>{chatList.length > 0 ? renderChatList() : null}</>;
};
